import React, { ReactNode, SVGProps, ComponentType } from 'react';
import { GREY_Q, RED_D } from '../../constants/Colors';
import Spinner from '../svgs/Spinner';
import X from '../svgs/X';
import { makeLazyLoader } from './Lazy';

interface IProps<T> extends SVGProps<SVGSVGElement> {
  children: (value: T) => ReactNode;
}

interface IOptions {
  LoadingComponent?: ComponentType<SVGProps<SVGSVGElement>>;
  ErrorComponent?: ComponentType<SVGProps<SVGSVGElement>>;
}

// Load anything with a friendly spinner. If loading fails, it's
// substituted with X icon. Any extra props apart from `load` and
// `children` are passed to SVG spinner and X icon. Useful for sizing,
// etc.
export function makeSpinnerLoader<T>(load: () => Promise<T>, {
  LoadingComponent = (props) => <Spinner {...props} color={GREY_Q} />,
  ErrorComponent = (props) => <X {...props} color={RED_D} />,
}: IOptions = {}) {
  const LazyLoader = makeLazyLoader(load);

  return function SpinnerLoader({ children, ...props }: IProps<T>) {
    return <LazyLoader>{({ value, error }) =>
      value ? children(value) :
      error ? <ErrorComponent {...props} /> :
      <LoadingComponent {...props} />
    }</LazyLoader>;
  };
}

export function makeSpinnerDefaultLoader<T>(load: () => Promise<{ default: T }>) {
  return makeSpinnerLoader(async () => (await load()).default);
}
